import React, { useCallback, useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import BlockIcon from '@material-ui/icons/Block';

const useStyles = makeStyles(theme => ({
  tableHead: {
    backgroundColor: theme.palette.background.default,
  },
  headCell: {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  nameCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  dataCell: {
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
  },
  levelChip: {
    height: 22,
    fontSize: '0.75rem',
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
  },
  emptyBox: {
    padding: theme.spacing(2),
  },
  actionCell: {
    width: 120,
    textAlign: 'right',
  },
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': { backgroundColor: theme.palette.error.dark },
  },
  progressBox: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

interface DeployedEnv {
  entityRef: string;
  name: string;
  title: string;
  level: number;
}

type RemoveStatus = 'idle' | 'queuing' | 'waiting' | 'cleanup' | 'done' | 'error';

export function AzureRemoveEnvironmentCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const meta = entity.metadata as Record<string, any>;
  const appName = entity.metadata.name;
  const componentRef = `component:${entity.metadata.namespace ?? 'default'}/${appName}`;
  const organization = meta.adoOrganization as string | undefined;
  const project = meta.adoProject as string | undefined;
  const repoName = meta.adoRepoName as string | undefined;

  const compSpec = entity.spec as Record<string, any>;
  const dependsOn: string[] = compSpec?.dependsOn ?? [];
  const envRefs = dependsOn.filter(r => r.toLowerCase().startsWith('azureenvironment:'));

  const [deployedEnvs, setDeployedEnvs] = useState<DeployedEnv[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [confirmEnv, setConfirmEnv] = useState<DeployedEnv | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [status, setStatus] = useState<RemoveStatus>('idle');
  const [pollStatus, setPollStatus] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [runUrl, setRunUrl] = useState('');

  const loadEnvs = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const envs: DeployedEnv[] = [];
      for (const ref of envRefs) {
        try {
          const envEntity = await catalogApi.getEntityByRef(ref);
          if (envEntity) {
            const envMeta = envEntity.metadata as Record<string, any>;
            envs.push({
              entityRef: ref,
              name: envEntity.metadata.name,
              title: (envEntity.metadata.title || envEntity.metadata.name) as string,
              level: Number(envMeta.level ?? 0),
            });
          }
        } catch {
          // skip unresolvable refs
        }
      }
      envs.sort((a, b) => a.level - b.level);
      setDeployedEnvs(envs);
    } catch (err: any) {
      setLoadError(err.message ?? 'Failed to load environments');
    } finally {
      setLoading(false);
    }
  }, [catalogApi, envRefs.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadEnvs();
  }, [loadEnvs]);

  /** Poll a single ADO run until completed. Returns 'succeeded' | 'failed'. */
  const waitForRun = async (
    baseUrl: string,
    token: string,
    runId: number,
    onProgress: (msg: string) => void,
  ): Promise<'succeeded' | 'failed'> => {
    const POLL_INTERVAL_MS = 10_000;
    for (;;) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      try {
        const res = await fetch(
          `${baseUrl}/pipelines/run-status?organization=${encodeURIComponent(organization!)}&project=${encodeURIComponent(project!)}&runId=${runId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) continue;
        const data = await res.json();
        onProgress(`${data.status}${data.result ? ` (${data.result})` : ''}`);
        if (data.status === 'completed') {
          return data.result === 'succeeded' ? 'succeeded' : 'failed';
        }
      } catch {
        // transient — keep polling
      }
    }
  };

  const handleRemoveConfirmed = async () => {
    if (!confirmEnv || !organization || !project) return;
    const env = confirmEnv;
    setConfirmEnv(null);
    setRemoving(env.entityRef);
    setStatus('queuing');
    setErrorMsg('');
    setPollStatus('');
    setRunUrl('');

    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token = '' } = await identityApi.getCredentials();
      const pipelineName = `cidp-${appName}-databricks-dlt-deploy`;

      // 1. Queue destroy pipeline for this specific environment
      const destroyRes = await fetch(`${baseUrl}/entities/destroy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityRef: componentRef,
          organization,
          project,
          pipelineName,
          targetEnvironment: env.name,
          repoName,
        }),
      });
      if (!destroyRes.ok) {
        const err = await destroyRes.json().catch(() => ({ error: destroyRes.statusText })) as any;
        throw new Error(err.error ?? `HTTP ${destroyRes.status}`);
      }
      const { runId, runUrl: url } = await destroyRes.json();
      setRunUrl(url);
      setStatus('waiting');

      // 2. Poll until done
      const result = await waitForRun(baseUrl, token, runId, msg => setPollStatus(msg));
      if (result === 'failed') {
        throw new Error(
          `Destroy pipeline failed. The environment has NOT been removed from the catalog. Check the pipeline run for details.`,
        );
      }

      // 3. Run cleanup
      setStatus('cleanup');
      const cleanupRes = await fetch(`${baseUrl}/components/remove-environment-cleanup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ componentRef, environmentRef: env.entityRef }),
      });
      if (!cleanupRes.ok) {
        const err = await cleanupRes.json().catch(() => ({ error: cleanupRes.statusText })) as any;
        throw new Error(err.error ?? `HTTP ${cleanupRes.status}`);
      }

      // 4. Optimistically remove from local state
      setDeployedEnvs(prev => prev.filter(e => e.entityRef !== env.entityRef));
      setStatus('idle');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err.message ?? 'Failed to remove environment');
    } finally {
      setRemoving(null);
    }
  };

  const isOperationInProgress = removing !== null && (
    status === 'queuing' || status === 'waiting' || status === 'cleanup'
  );

  const isSingleEnv = deployedEnvs.length === 1;

  /** Returns the block reason for removing an env, or undefined if removal is allowed. */
  const getRemoveBlockReason = (env: DeployedEnv): string | undefined => {
    if (isSingleEnv) {
      return 'This application cannot be disconnected from its only environment.';
    }
    const higherEnv = deployedEnvs.find(e => e.level > env.level);
    if (higherEnv) {
      return `Remove "${higherEnv.title}" (Level ${higherEnv.level}) first before removing this environment.`;
    }
    return undefined;
  };

  return (
    <InfoCard title="Deployed Environments" variant="gridItem">
      {loading && <Progress />}

      {!loading && loadError && (
        <Box className={classes.emptyBox}>
          <Typography className={classes.errorText}>{loadError}</Typography>
          <Button size="small" onClick={loadEnvs} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </Box>
      )}

      {!loading && !loadError && deployedEnvs.length === 0 && (
        <Box className={classes.emptyBox}>
          <Typography variant="body2" color="textSecondary">
            This application is not deployed to any environments yet.
          </Typography>
        </Box>
      )}

      {!loading && !loadError && deployedEnvs.length > 0 && (
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.headCell}>Environment</TableCell>
              <TableCell className={classes.headCell}>Level</TableCell>
              <TableCell className={classes.actionCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {deployedEnvs.map(env => {
              const isRemoving = removing === env.entityRef;
              const blockReason = getRemoveBlockReason(env);
              const isBlocked = !!blockReason;
              return (
                <TableRow key={env.entityRef}>
                  <TableCell className={classes.nameCell}>{env.title}</TableCell>
                  <TableCell className={classes.dataCell}>
                    <Chip label={`Level ${env.level}`} size="small" className={classes.levelChip} />
                  </TableCell>
                  <TableCell className={classes.actionCell}>
                    {isRemoving ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Tooltip title={blockReason ?? ''} disableHoverListener={!isBlocked}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={isBlocked ? <BlockIcon /> : <RemoveCircleOutlineIcon />}
                            onClick={() => !isBlocked && setConfirmEnv(env)}
                            disabled={isOperationInProgress || isBlocked}
                          >
                            Remove
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* In-progress status banner */}
      {isOperationInProgress && (
        <Box className={classes.progressBox}>
          <Progress />
          <Typography variant="body2">
            {status === 'queuing' && 'Queuing destroy pipeline…'}
            {status === 'waiting' && (
              <>
                Running destroy pipeline…{' '}
                {runUrl && (
                  <a href={runUrl} target="_blank" rel="noopener noreferrer">
                    View pipeline run →
                  </a>
                )}
              </>
            )}
            {status === 'cleanup' && 'Removing deployment stage and severing catalog relationship…'}
          </Typography>
          {pollStatus && (
            <Typography variant="caption" color="textSecondary">
              {pollStatus}
            </Typography>
          )}
        </Box>
      )}

      {status === 'error' && errorMsg && (
        <Box px={2} pb={1}>
          <Typography className={classes.errorText}>{errorMsg}</Typography>
          <Button
            size="small"
            onClick={() => { setStatus('idle'); setErrorMsg(''); }}
            style={{ marginTop: 8 }}
          >
            Dismiss
          </Button>
        </Box>
      )}

      {/* Confirmation dialog */}
      <Dialog open={!!confirmEnv} onClose={() => setConfirmEnv(null)} maxWidth="xs">
        <DialogTitle>Remove from Environment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove this application from <strong>{confirmEnv?.title}</strong>?
          </DialogContentText>
          <DialogContentText component="div">
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Run the application pipeline with destroy for this environment only</li>
              <li>Wait for the destroy pipeline to complete successfully</li>
              <li>Remove this environment's deployment stages from the application pipeline</li>
              <li>Sever the relationship in the catalog (<code>spec.dependsOn</code>)</li>
            </ul>
          </DialogContentText>
          <DialogContentText color="error">
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEnv(null)}>Cancel</Button>
          <Button
            className={classes.dangerButton}
            variant="contained"
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={handleRemoveConfirmed}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </InfoCard>
  );
}
